import { Fragment, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { Dialog, Popover, Tab, Transition, Menu } from "@headlessui/react";
import {
  MenuIcon,
  ViewGridAddIcon,
  ViewGridIcon,
  XIcon,
} from "@heroicons/react/outline";
import Logout from "../Logout";
const navigation = {
  categories: [
    {
      name: "Agents",
      featured: [
        {
          name: "Agents List",
          href: "/manager/agents/",
          icon: ViewGridIcon,
        },
        {
          name: "Add Agents",
          href: "/manager/add-agent/",
          icon: ViewGridAddIcon,
        },
      ],
    },
  ],
  pages: [{ name: "Orders", href: "/manager/orders-list" }],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ManagerLayout() {
  const [modal, setModal] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="sticky top-0 bg-white z-50">
        {/* Mobile menu */}
        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-40 lg:hidden"
            onClose={setOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative max-w-xs w-full bg-white shadow-xl pb-12 flex flex-col overflow-y-auto">
                <div className="px-4 pt-5 pb-2 flex">
                  <button
                    type="button"
                    className="-m-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Links */}
                <Tab.Group as="div" className="mt-2">
                  <div className="border-b border-gray-200">
                    <Tab.List className="-mb-px flex px-4 space-x-8">
                      {navigation.categories.map((category) => (
                        <Tab
                          key={category.name}
                          className={({ selected }) =>
                            classNames(
                              selected
                                ? "text-cyan-600 border-cyan-600"
                                : "text-cyan-900 border-transparent",
                              "flex-1 whitespace-nowrap py-4 px-1 border-b-2 text-base font-normal"
                            )
                          }
                        >
                          {category.name}
                        </Tab>
                      ))}
                    </Tab.List>
                  </div>
                  <Tab.Panels as={Fragment}>
                    {navigation.categories.map((category) => (
                      <Tab.Panel
                        key={category.name}
                        className="px-4 py-6 space-y-12"
                      >
                        <div className="grid grid-cols-2 gap-x-4 gap-y-10">
                          {category.featured.map((item) => (
                            <Link to={item.href} key={item.name}>
                              <div className="group relative border rounded-md border-gray-200 hover:border-cyan-400">
                                <div className="p-4 hover:bg-cyan-50">
                                  <div className="aspect-w-1 aspect-h-1 rounded-md overflow-hidden group-hover:opacity-75">
                                    {
                                      <item.icon className="h-8 w-8 mx-auto text-gray-600" />
                                    }
                                  </div>
                                  <p className="mt-6 block text-sm font-normal text-gray-900">
                                    <span
                                      className="absolute z-10 inset-0"
                                      aria-hidden="true"
                                    />
                                    {item.name}
                                  </p>
                                  <p
                                    aria-hidden="true"
                                    className="mt-1 text-sm text-gray-500"
                                  >
                                    Shop now
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </Tab.Panel>
                    ))}
                  </Tab.Panels>
                </Tab.Group>
                <div className="border-t border-gray-200 py-6 px-4 space-y-6">
                  {navigation.pages.map((page) => (
                    <Link to={page.href} key={page.name}>
                      <div className="flow-root">
                        <p className="-m-2 p-2 block font-normal text-cyan-900">
                          {page.name}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition.Root>

        <header className="relative">
          <nav aria-label="Top">
            {/* Secondary navigation */}
            <div className="bg-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="border-b border-gray-200">
                  <div className="h-16 flex items-center justify-between">
                    {/* Logo (lg+) */}
                    <div className="hidden lg:flex-1 lg:flex lg:items-center">
                      <Link to="/">
                        <span className="sr-only">Workflow</span>
                        <h1 className="font-light text-xl text-cyan-700">
                          EVD Management
                        </h1>
                      </Link>
                    </div>

                    <div className="hidden h-full lg:flex">
                      {/* Flyout menus */}
                      <Popover.Group className="px-4 bottom-0 inset-x-0">
                        <div className="h-full flex items-center justify-center space-x-8">
                          {navigation.categories.map((category) => (
                            <Popover key={category.name} className="flex">
                              {({ open }) => (
                                <>
                                  <div className="relative flex">
                                    <Popover.Button
                                      className={classNames(
                                        open
                                          ? "border-cyan-600 text-cyan-600"
                                          : "border-transparent text-cyan-700 hover:text-cyan-800",
                                        "relative z-10 flex items-center transition-colors ease-out duration-200 text-sm font-normal border-b-2 -mb-px pt-px"
                                      )}
                                    >
                                      {category.name}
                                    </Popover.Button>
                                  </div>

                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-200"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="transition ease-in duration-150"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Popover.Panel className="absolute top-full inset-x-0 text-sm text-gray-500">
                                      {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                                      <div
                                        className="absolute inset-0 top-1/2 bg-white shadow"
                                        aria-hidden="true"
                                      />

                                      <div className="relative bg-gray-50">
                                        <div className="max-w-7xl mx-auto px-8">
                                          <div className="grid grid-cols-2 gap-y-10 gap-x-8 py-16">
                                            {category.featured.map((item) => (
                                              <div
                                                key={item.name}
                                                className="group relative border-2 rounded-md border-gray-300 hover:border-cyan-400"
                                              >
                                                <div className="group p-4 hover:bg-cyan-50">
                                                  <div className="aspect-w-1 aspect-h-1 overflow-hidden">
                                                    <item.icon className="h-1/4 w-1/4 mx-auto text-gray-400 group-hover:text-cyan-600" />
                                                  </div>
                                                  <a
                                                    href={item.href}
                                                    className="mt-4 block font-medium group-hover:text-cyan-600"
                                                  >
                                                    <span
                                                      className="absolute z-10 inset-0"
                                                      aria-hidden="true"
                                                    />
                                                    {item.name}
                                                  </a>
                                                  <p
                                                    aria-hidden="true"
                                                    className="mt-1 group-hover:text-cyan-600"
                                                  >
                                                    Shop now
                                                  </p>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </Popover.Panel>
                                  </Transition>
                                </>
                              )}
                            </Popover>
                          ))}
                          {navigation.pages.map((page) => (
                            <Link to={page.href} key={page.name}>
                              <p className="flex items-center text-sm font-normal text-cyan-900">
                                {page.name}
                              </p>
                            </Link>
                          ))}
                        </div>
                      </Popover.Group>
                    </div>

                    {/* Mobile menu and search (lg-) */}
                    <div className="flex-1 flex items-center lg:hidden">
                      <button
                        type="button"
                        className="-ml-2 bg-white p-2 rounded-md text-gray-400"
                        onClick={() => setOpen(true)}
                      >
                        <span className="sr-only">Open menu</span>
                        <MenuIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Logo (lg-) */}
                    <Link to="/" className="lg:hidden">
                      <span className="sr-only">Workflow</span>
                      <h3 className="font-light text-cyan-700">
                        EVD Management
                      </h3>
                    </Link>

                    <div className="flex-1 flex items-center justify-end">
                      <Menu as="div" className="ml-3 relative">
                        <div>
                          <Menu.Button className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500">
                            <span className="sr-only">Open user menu</span>
                            <img
                              className="h-8 w-8 rounded-full"
                              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                              alt=""
                            />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {/* <Menu.Item>
                              {({ active }) => (
                                <a
                                  href="#idkwheretogo"
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    "block px-4 py-2 text-sm text-gray-700"
                                  )}
                                >
                                  Your Profile
                                </a>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <a
                                  href="#idkwheretogo"
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    "block px-4 py-2 text-sm text-gray-700"
                                  )}
                                >
                                  Settings
                                </a>
                              )}
                            </Menu.Item> */}

                            <Menu.Item>
                              <button
                                className="block py-2 px-4 text-sm text-gray-700"
                                onClick={() => setModal(true)}
                              >
                                {" "}
                                Logout
                              </button>
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </header>
      </div>
      <div>
        <Outlet />
      </div>
      {modal && <Logout modal={modal} setModal={setModal} />}
    </>
  );
}
